<template>
  <v-dialog v-model="dialog"
            max-width="600px"
            persistent
  >
    <v-card>
      <v-toolbar color="primary"
                 dark
                 flat
                 dense
      >
        <v-toolbar-title class="text-h6">
          Nova Credencial de Integração
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon
               :disabled="saving"
               @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <!-- Input Form -->
        <v-form v-if="!createdCredentials"
                ref="form"
                v-model="valid"
        >
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="formData.name"
                label="Nome/Descrição da Credencial"
                placeholder="Ex: RD Station Produção"
                :rules="[v => !!v || 'Campo obrigatório']"
                required
                dense
                outlined
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="formData.integrationId"
                :items="integrations"
                item-text="name"
                item-value="_id"
                label="Integração (Tipo)"
                :rules="[v => !!v || 'Campo obrigatório']"
                required
                dense
                outlined
                clearable
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="formData.selectedCustomers"
                :items="customerList"
                item-text="name"
                item-value="_id"
                label="Clientes Associados (Bancos de Dados)"
                multiple
                chips
                small-chips
                deletable-chips
                dense
                outlined
                :rules="[v => v && v.length > 0 || 'Selecione pelo menos um cliente']"
                hint="Quais clientes esta credencial terá acesso?"
                persistent-hint
              >
                <template #selection="{ item, index }">
                  <v-chip v-if="index === 0">
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <span v-if="index === 1"
                        class="grey--text text-caption ml-2"
                  >
                    (+{{ formData.selectedCustomers.length - 1 }} clientes)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-form>

        <!-- Result Dialog -->
        <div v-else
             class="text-center py-4"
        >
          <v-icon color="success"
                  size="64"
                  class="mb-4"
          >
            mdi-check-circle
          </v-icon>
          <h3 class="text-h5 font-weight-bold mb-2">
            Credencial Gerada com Sucesso!
          </h3>
          <p class="red--text font-weight-bold mb-6">
            Guarde o Client Secret agora. Ele NÃO será exibido novamente por motivos de segurança.
          </p>

          <v-text-field
            v-model="createdCredentials.clientId"
            label="Client ID (Username)"
            readonly
            outlined
            dense
            append-icon="mdi-content-copy"
            @click:append="copyToClipboard(createdCredentials.clientId, 'Client ID')"
          />

          <v-text-field
            v-model="createdCredentials.clientSecret"
            label="Client Secret (Password)"
            readonly
            outlined
            dense
            append-icon="mdi-content-copy"
            @click:append="copyToClipboard(createdCredentials.clientSecret, 'Client Secret')"
          />

          <v-btn
            color="primary"
            outlined
            block
            class="rounded-pill"
            @click="copyToClipboard(formattedCredentials, 'Credenciais')"
          >
            <v-icon left>
              mdi-content-copy
            </v-icon>
            Copiar Tudo
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          v-if="!createdCredentials"
          color="error"
          text
          class="rounded-pill px-4"
          :disabled="saving"
          @click="close"
        >
          Cancelar
        </v-btn>
        <v-btn
          v-if="!createdCredentials"
          color="primary"
          class="rounded-pill px-4"
          :disabled="!valid"
          :loading="saving"
          @click="save"
        >
          Gerar Credencial
        </v-btn>
        <v-btn
          v-if="createdCredentials"
          color="primary"
          class="rounded-pill px-4"
          @click="close"
        >
          Concluir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { $k } from '@/lib/kraken'
  import { mapActions, mapGetters } from 'vuex'

  export default {
    name: 'CreateCredentialDialog',
    data: () => ({
      dialog: false,
      valid: false,
      saving: false,
      integrations: [],
      formData: {
        name: '',
        integrationId: null,
        selectedCustomers: [],
      },
      createdCredentials: null,
    }),
    computed: {
      ...mapGetters('customers', ['customerList']),
      formattedCredentials() {
        if (!this.createdCredentials) return ''
        return [
          'legacy_url: https://legacy.clinic.com.br/v2/legacy',
          `username: ${this.createdCredentials.clientId}`,
          `password: ${this.createdCredentials.clientSecret}`,
        ].join('\n')
      },
    },
    methods: {
      ...mapActions('customers', ['listCustomers']),
      async openDialog() {
        this.dialog = true
        this.valid = false
        this.saving = false
        this.createdCredentials = null
        this.formData = {
          name: '',
          integrationId: null,
          selectedCustomers: [],
        }
        if (this.$refs.form) this.$refs.form.resetValidation()

        await Promise.all([
          this.fetchIntegrations(),
          this.listCustomers(),
        ])
      },
      close() {
        if (this.createdCredentials) {
          this.$emit('created')
        }
        this.dialog = false
      },
      async fetchIntegrations() {
        try {
          const res = await $k.manage.integrations.get()
          this.integrations = res.results || res.result || res || []
        } catch (error) {
          console.error('Falha ao carregar integracoes', error)
        }
      },
      generateClientSecret() {
        const array = new Uint32Array(8)
        window.crypto.getRandomValues(array)
        return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('')
      },
      async save() {
        if (!this.$refs.form.validate()) return

        this.saving = true
        try {
          const integration = this.integrations.find(i => i._id === this.formData.integrationId)
          const shortName = (integration && integration.shortName) ? integration.shortName.toLowerCase().replace(/[^a-z0-9]/g, '') : 'unk'
          const smallSeed = Math.random().toString(36).substring(2, 8)
        
          const clientId = `i-legacy-${shortName}-${smallSeed}.clinic.inf.br`
          const clientSecret = this.generateClientSecret()

          // 1. Criar o usuário (POST)
          const userRes = await $k.manage.users.post({
            name: this.formData.name,
            username: clientId,
            password: clientSecret,
            scope: 'integration',
          })

          const userId = userRes.user_data._id

          // 2. Mapear os clientes selecionados para o formato de clinicUser
          const associatedClinicUsers = this.formData.selectedCustomers.map(customerId => {
            const customer = this.customerList.find(c => c._id === customerId)
            return {
              enabled: true,
              customerId: customerId,
              customerCode: customer.code,
              clinicUser: `i:${this.formData.integrationId}`,
              clinicUsername: this.formData.name,
              service: 'legacy',
            }
          })

          // 3. Atualizar o usuário recém criado com as regras de conta e clientes (PUT)
          await $k.manage.users._id.put({
            id: userId,
            accountRule: 'Integration',
            services: {
              integration: {
                enabled: true,
                integrationId: this.formData.integrationId,
              },
            },
            associatedClinicUsers: associatedClinicUsers,
          })

          // Sucesso! Mostrar credenciais na tela
          this.createdCredentials = {
            clientId,
            clientSecret,
          }
        } catch (error) {
          console.error(error)
          if (this.$alert) this.$alert('error', 'Erro ao gerar credencial')
        } finally {
          this.saving = false
        }
      },
      async copyToClipboard(text, label) {
        try {
          await navigator.clipboard.writeText(text)
          if (this.$alert) this.$alert('success', `${label} copiado!`)
        } catch (err) {
          console.error('Falha ao copiar', err)
        }
      },
    },
  }
</script>

<style scoped>
.v-btn.rounded-pill {
  border-radius: 50px !important;
  text-transform: none !important;
  font-weight: 600;
}
</style>
